/** @jsxImportSource @emotion/react */
import { autoUpdate, flip, offset, shift, size, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import PropTypes from 'prop-types';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Checkbox from 'components/global/Checkbox';
import Icon from 'components/global/Icon';
import ReactPortal from 'components/global/ReactPortal';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { FORM } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints, POPPER_CONFIG } from 'utils/const.styles';

import { faAngleDown, faCircleNotch, faExclamationCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import SearchInput from './SearchInput';
import ViewField from './ViewField';
import * as S from './styled/EnumSelect.styled';
import { InputMessageContainer } from './styled/InputCommon.styled';

const EnumSelect = ({
	placeholder,
	enumName,
	value,
	setValue,
	name = '',
	errorMessage: errorMessageProp,
	error,
	disabled,
	multipleSelection,
	viewMode,
	style,
	className,
	css,
	searchable = true,
	valueRemovable = true,
	data,
	fixedContainerWidth = true,
	firstItem,
	showMultipleSelectionButtons = false,
	onApply,
	fetchFunction,
	reFetchOnSearch = false,
	labelPrefix,
	forcePrefix = false,
	renderSelectionValue,
	renderOption,
	searchFunction,
	noItemsPlaceholder = 'noItems',
	emptyStateComponent,
	searchInputPlaceholder
}) => {
	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const [open, setOpen] = useState(false);
	const [maxHeight, setMaxHeight] = useState(null);
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const getPrefixedValue = useCallback(
		(value) => {
			if (labelPrefix) {
				const valueWithPrefix = `${labelPrefix ?? ''}${value}`;
				const translatedValueWithPrefix = t(valueWithPrefix);
				if (translatedValueWithPrefix !== valueWithPrefix || forcePrefix) {
					return `${labelPrefix ?? ''}${value}`;
				}
			}

			return value;
		},
		[labelPrefix]
	);

	const calculatedLocalValue = useMemo(
		() =>
			value
				? multipleSelection
					? value.length === 0
						? placeholder
						: value.map((item) => t(getPrefixedValue(item))).join(', ')
					: getPrefixedValue(value)
				: placeholder,
		[value, labelPrefix, getPrefixedValue, placeholder, multipleSelection]
	);

	const [searchValue, setSearchValue] = useState(null);

	const [localData, setLocalData] = useState(data ?? []);
	const [filteredData, setFilteredData] = useState(localData);
	const [loading, setLoading] = useState(data === undefined);

	const { showError } = useAlert();

	const fetchData = async () => {
		setLoading(true);
		if (fetchFunction) {
			const data = await fetchFunction();
			setLocalData(data);
		} else if (enumName) {
			await axiosPrivate
				.post(FORM.GET_ENUM, {
					name: enumName
				})
				.then((response) => {
					if (response?.status === 200) {
						setLocalData(response.data);
					} else {
						AppLogger.error(response);
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError();
				});
		} else {
			AppLogger.error('EnumSelect: enumName, fetchFunction is not defined');
			showError();
		}

		setLoading(false);
	};

	useUpdateEffect(() => {
		setLocalData(data);
		if (data) {
			setLoading(false);
		}
	}, [data]);

	useEffect(() => {
		if (open && data === undefined) {
			fetchData();
		}
	}, [enumName, open]);

	const middleware = useMemo(
		() => [
			flip({
				padding: 10
			}),
			shift({
				mainAxis: false
			}),
			offset({
				mainAxis: 8,
				crossAxis: 0
			}),
			size({
				apply({ availableHeight, rects, x, elements }) {
					if (fixedContainerWidth) {
						elements.floating.style.width = `${rects.reference.width}px`;
					} else if (isMobile) {
						const popperWidth = document.documentElement.clientWidth - 2 * POPPER_CONFIG.MARGIN;
						elements.floating.style.width = `${popperWidth}px`;
						elements.floating.style.left = `${POPPER_CONFIG.MARGIN - x}px`;
					}
					flushSync(() => setMaxHeight(availableHeight));
				}
			})
		],
		[fixedContainerWidth, isMobile]
	);

	const handleOpenChange = (value) => {
		setOpen(value);
		if (!value) {
			setSearchValue(null);
		}
	};

	const { refs, floatingStyles, context } = useFloating({
		placement: 'bottom-start',
		middleware,
		open,
		onOpenChange: handleOpenChange,
		whileElementsMounted: autoUpdate
	});
	const dismiss = useDismiss(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

	const closePopper = () => {
		if (!open) return;
		setOpen(false);
		setSearchValue(null);
	};

	const handleButtonClick = () => {
		setOpen(!open);
	};

	const handleItemClick = (event, item) => {
		event.stopPropagation();

		setOpen(false);
		setSearchValue(null);
		setValue(item);
	};

	useUpdateEffect(() => {
		let timeout;

		if (searchValue) {
			if (reFetchOnSearch) {
				timeout = setTimeout(() => {
					setLoading(true);

					fetchFunction(searchValue)
						.then((data) => {
							setFilteredData(data);
						})
						.finally(() => {
							setLoading(false);
						});
				}, 300);
			} else {
				setFilteredData(
					localData?.filter((item) =>
						searchFunction
							? searchFunction(item, searchValue)
							: t(getPrefixedValue(item)).toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
					)
				);
			}
		} else {
			setFilteredData(localData);
		}

		return () => {
			if (timeout) {
				clearTimeout(timeout);
				setLoading(false);
			}
		};
	}, [searchValue, localData]);

	const handleCheckboxChange = (event, item) => {
		event.stopPropagation();

		if (value === null) {
			setValue([item]);
		} else if (!value.includes(item)) {
			setValue([...value, item]);
		} else {
			const filteredValue = value?.filter((val) => val !== item);
			setValue(filteredValue.length ? filteredValue : null);
		}
	};

	const handleDelete = (e) => {
		e.stopPropagation();

		setValue(null);
	};

	const handleFirstItemClick = () => {
		firstItem?.action && firstItem?.action();
		!firstItem.shouldKeepOpen && closePopper();
	};

	const handleResetClick = () => {
		setValue(null);
	};

	const handleApplyClick = () => {
		closePopper();
		onApply && onApply();
	};

	const handleSelectAll = (isAllSelected) => {
		if (isAllSelected) {
			setValue(null);
		} else {
			setValue(filteredData);
		}
	};

	const renderSelectAll = () => {
		const isAllSelected = value?.length > 0 && filteredData.every((item) => value?.includes(item));

		return (
			<S.MultipleSelectionItem
				onMouseDown={(event) => event.stopPropagation()}
				onTouchEnd={(event) => event.stopPropagation()}
				onClick={() => handleSelectAll(isAllSelected)}
				className={isAllSelected ? 'selected' : ''}>
				<Checkbox checked={isAllSelected} readOnly />
				<Typography>enumSelect.selectAll</Typography>
			</S.MultipleSelectionItem>
		);
	};

	return (
		<>
			<S.EnumSelectContainer
				className={`enumSelect ${errorMessage ? 'error' : ''} ${
					name ? `enumselect-class-selector-${name}` : ''
				}`}
				viewmode={viewMode || disabled}>
				{viewMode || disabled ? (
					<ViewField
						value={calculatedLocalValue !== placeholder ? calculatedLocalValue : '-'}
						customValue={
							calculatedLocalValue !== placeholder && renderSelectionValue
								? renderSelectionValue(calculatedLocalValue, localData)
								: undefined
						}
						disabled={disabled}
					/>
				) : (
					<>
						<S.EnumSelectWrapper
							ref={refs.setReference}
							style={style}
							className={className}
							css={css}
							{...getReferenceProps()}>
							<S.Label
								className={`enumselect-label ${errorMessage || error ? 'error' : ''} ${
									open ? 'open' : ''
								}`}
								onClick={handleButtonClick}>
								{calculatedLocalValue !== placeholder && renderSelectionValue ? (
									renderSelectionValue(calculatedLocalValue, localData)
								) : (
									<Typography
										fontWeight={calculatedLocalValue !== placeholder ? '500' : '400'}
										color={
											calculatedLocalValue !== placeholder ? 'var(--black)' : 'var(--content-4)'
										}>
										{calculatedLocalValue}
									</Typography>
								)}
								<S.IconContainer>
									{calculatedLocalValue !== placeholder && valueRemovable && (
										<Icon icon={faTimes} onClick={(e) => handleDelete(e)} />
									)}
									<Icon icon={faAngleDown} />
								</S.IconContainer>
							</S.Label>
						</S.EnumSelectWrapper>
						{open && (
							<ReactPortal wrapperId={'enumselect-portal'}>
								<S.EnumSelectContentContainer
									ref={refs.setFloating}
									style={floatingStyles}
									{...getFloatingProps()}>
									{searchable && (
										<SearchInput
											autoFocus
											value={searchValue}
											setValue={setSearchValue}
											onMouseDown={(event) => event.stopPropagation()}
											placeholder={searchInputPlaceholder}
										/>
									)}
									<S.EnumSelectContentWrapper
										maxHeight={maxHeight}
										searchable={searchable}
										showMultipleSelectionButtons={
											showMultipleSelectionButtons && multipleSelection
										}>
										{firstItem && (
											<S.EnumSelectItem
												onMouseDown={(event) => event.stopPropagation()}
												onTouchEnd={(event) => event.stopPropagation()}
												onClick={handleFirstItemClick}>
												{firstItem.label}
											</S.EnumSelectItem>
										)}
										{loading ? (
											<S.Loading>
												<Icon icon={faCircleNotch} className="fa-spin" color="var(--blue)" />
											</S.Loading>
										) : !filteredData || filteredData?.length === 0 ? (
											!searchValue && emptyStateComponent ? (
												emptyStateComponent(closePopper)
											) : (
												<S.EnumSelectNoItems>
													<Typography color="var(--content-3)">
														{noItemsPlaceholder}
													</Typography>
												</S.EnumSelectNoItems>
											)
										) : (
											<>
												{multipleSelection && searchValue === null && renderSelectAll()}
												{filteredData.map((item, index) => {
													if (multipleSelection) {
														const selected = value?.includes(item) ?? false;
														return (
															<S.MultipleSelectionItem
																key={index}
																onMouseDown={(event) => event.stopPropagation()}
																onTouchEnd={(event) => event.stopPropagation()}
																onClick={(event) => handleCheckboxChange(event, item)}
																className={selected ? 'selected' : ''}>
																<Checkbox checked={selected} readOnly />
																{renderOption ? (
																	renderOption(item, index)
																) : (
																	<Typography>{getPrefixedValue(item)}</Typography>
																)}
															</S.MultipleSelectionItem>
														);
													}
													if (item !== calculatedLocalValue) {
														return (
															<S.EnumSelectItem
																key={index}
																onMouseDown={(event) => event.stopPropagation()}
																onTouchEnd={(event) => event.stopPropagation()}
																onClick={(event) => handleItemClick(event, item)}>
																{renderOption ? (
																	renderOption(item, index)
																) : (
																	<Typography>{getPrefixedValue(item)}</Typography>
																)}
															</S.EnumSelectItem>
														);
													} else {
														if (filteredData.length === 1) {
															return !searchValue && emptyStateComponent ? (
																emptyStateComponent
															) : (
																<S.EnumSelectNoItems key={index}>
																	<Typography color="var(--content-3)">
																		{noItemsPlaceholder}
																	</Typography>
																</S.EnumSelectNoItems>
															);
														}
													}
												})}
											</>
										)}
									</S.EnumSelectContentWrapper>
									{showMultipleSelectionButtons && multipleSelection && (
										<S.EnumSelectMultipleButtonContainer>
											{value?.length > 0 && (
												<Button
													variant="transparent"
													color="red"
													label="reset"
													onMouseDown={(event) => event.stopPropagation()}
													onClick={handleResetClick}
												/>
											)}
											<Button
												variant="black"
												label="apply"
												onMouseDown={(event) => event.stopPropagation()}
												onClick={handleApplyClick}
											/>
										</S.EnumSelectMultipleButtonContainer>
									)}
								</S.EnumSelectContentContainer>
							</ReactPortal>
						)}
					</>
				)}
			</S.EnumSelectContainer>
			{errorMessage && (
				<InputMessageContainer className="error-message-container enumselect-error-message-container">
					<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
					<Typography variant="caption" color="var(--red)" noWrap>
						{errorMessage}
					</Typography>
				</InputMessageContainer>
			)}
		</>
	);
};

EnumSelect.propTypes = {
	/** Placeholder of the label. */
	placeholder: PropTypes.string,
	/** Required enum name for fetch data from enum api. */
	enumName: PropTypes.string,
	/** Ready data. */
	value: PropTypes.any,
	/** Fired when user select an option. */
	setValue: PropTypes.func,
	/** Name of the input. */
	name: PropTypes.string,
	/** Gets string for show error message. */
	errorMessage: PropTypes.string,
	/** If error comes true just paint the input */
	error: PropTypes.bool,
	/** Disable the selection. */
	disabled: PropTypes.bool,
	/** Default value is false. If set true show checkboxed for multiple selection. */
	multipleSelection: PropTypes.bool,
	/* If the view mode is true no input will show up just the string will be shown.  */
	viewMode: PropTypes.bool,
	/** html style prop of the enumselect wrapper */
	style: PropTypes.object,
	/** html className prop of the enumselect wrapper */
	className: PropTypes.string,
	/** css prop of the enumselect wrapper */
	css: PropTypes.object,
	/** If false search container does not render. Default value is true. */
	searchable: PropTypes.bool,
	/** If false cross icon for remove value does not render. Default value is true. */
	valueRemovable: PropTypes.bool,
	/** Data array for show items in enum select component. */
	data: PropTypes.array,
	/** If true container width will be fixed. Default value is true. */
	fixedContainerWidth: PropTypes.bool,
	/* It is an object that has a label and an action for adding a first item to options */
	firstItem: PropTypes.exact({
		action: PropTypes.func,
		label: PropTypes.element
	}),
	/** If true show apply and reset button for multiple selection. */
	showMultipleSelectionButtons: PropTypes.bool,
	/** Fired when user click on apply button. */
	onApply: PropTypes.func,
	/** Function for fetch data from api. */
	fetchFunction: PropTypes.func,
	/** If true data will be fetched again when user search. */
	reFetchOnSearch: PropTypes.bool,
	/** Prefix for label. */
	labelPrefix: PropTypes.string,
	/** If true prefix will be added to label even if it is not translated. */
	forcePrefix: PropTypes.bool,
	/** Function for render selected value. */
	renderSelectionValue: PropTypes.func,
	/** Function for render options. */
	renderOption: PropTypes.func,
	/** Function for search in options. */
	searchFunction: PropTypes.func,
	/** Placeholder for no items. */
	noItemsPlaceholder: PropTypes.string,
	/** Empty state component. */
	emptyStateComponent: PropTypes.func,
	/** Placeholder for search input. */
	searchInputPlaceholder: PropTypes.string
};

export default EnumSelect;
